import { Link } from "react-router-dom";

import { cn } from "@/lib/utils";
import type { AdminScope } from "@/components/layout/nav-config";

// The "Admin scope" block, transcribed from the seven client-validated admin
// mockups (Screens 10, 20, 21, 22, 23, 24, 33 — DW-401, Story 15.7 AC16).
//
// It is present on ALL SEVEN, always as the first child of
// `<aside class="sidebar">`, above the nav group. It was never built, and the
// recorded justification for omitting it — that a sidebar with one admin screen
// per scope had nothing to switch between — is what this story discharges.
//
// TWO MOCKUP INCONSISTENCIES ARE RESOLVED RATHER THAN COPIED:
//
//  1. `aria-current` is present on Screens 10, 23 and 24 and absent on 20, 21,
//     22 and 33 — with the SAME option marked in every case. That is
//     transcription drift, not a distinction: an unmarked current option is an
//     accessibility defect and the absence carries no meaning those four screens
//     could have intended. It ships on all seven — but as `aria-current="true"`
//     rather than the mockups' `"page"`, because this control selects a SECTION
//     and the marked option is usually not the page being viewed (an operator on
//     `/admin/users` has Individual selected while `/admin/console` is where that
//     option leads).
//  2. Three hint sentences for two scopes. Screen 24's is a genuine per-screen
//     variant (a reader inside a tenant record is in a different position from
//     one scanning the directory) and ships as one; see {@link adminScopeHint}.

const INDIVIDUAL_PATH = "/admin/console";
const ORGANIZATIONS_PATH = "/admin/orgs";

/** Verbatim from the mockups' `admin-scope-toggle` wrapper. */
export const ADMIN_SCOPE_GROUP_LABEL =
  "Switch between individual and organization administration";

export const INDIVIDUAL_SCOPE_LABEL = "Individual clinicians";
export const ORGANIZATIONS_SCOPE_LABEL = "Organizations";

/** Screens 10 / 20 / 21 / 22. */
export const INDIVIDUAL_SCOPE_HINT =
  "Clinician accounts: subscriptions, calibration, and access — not enterprise seat contracts.";
/** Screens 23 / 33. */
export const ORGANIZATIONS_SCOPE_HINT =
  "Enterprise directory: tenants, seat pools, and renewals — not per-clinician drill-down (use Individual).";
/** Screen 24 only — inside one tenant's record. */
export const ORGANIZATION_DETAIL_SCOPE_HINT =
  "You are inside a tenant record. Use Individual scope for per-clinician support.";

/**
 * The hint sentence for the current position.
 *
 * Screen 24's variant is keyed on being inside a tenant RECORD, which is a
 * different fact from being in the Organizations scope: `/admin/orgs` (Screen 23)
 * and `/admin/orgs/new` (Screen 33) are both in that scope and both carry the
 * directory sentence. The literal `new` segment is the provisioning form, not an
 * organization id — the router orders it ahead of `:orgId` for the same reason.
 */
export function adminScopeHint(pathname: string, scope: AdminScope): string {
  if (scope !== "organizations") return INDIVIDUAL_SCOPE_HINT;
  const insideRecord =
    pathname.startsWith(`${ORGANIZATIONS_PATH}/`) && pathname !== `${ORGANIZATIONS_PATH}/new`;
  return insideRecord ? ORGANIZATION_DETAIL_SCOPE_HINT : ORGANIZATIONS_SCOPE_HINT;
}

interface AdminScopeSelectorProps {
  scope: AdminScope;
  pathname: string;
  /** Fired after a scope is chosen (used to close the mobile drawer). */
  onNavigate?: () => void;
}

/**
 * The scope toggle. A pair of LINKS, as the mockups have it — choosing a scope
 * navigates to that scope's landing screen, so the sidebar and the page it
 * frames can never disagree about which estate the operator is looking at.
 */
export function AdminScopeSelector({ scope, pathname, onNavigate }: AdminScopeSelectorProps) {
  const options = [
    { key: "individual", label: INDIVIDUAL_SCOPE_LABEL, to: INDIVIDUAL_PATH },
    { key: "organizations", label: ORGANIZATIONS_SCOPE_LABEL, to: ORGANIZATIONS_PATH },
  ] as const;

  return (
    <div className="flex flex-col gap-2">
      <div className="px-1 text-xs font-semibold uppercase tracking-wide text-[color:var(--text-tertiary)]">
        Admin scope
      </div>
      <div
        role="group"
        aria-label={ADMIN_SCOPE_GROUP_LABEL}
        className="flex flex-col gap-0.5 rounded-[var(--radius-sm)] bg-[color:var(--bg-tertiary)] p-1"
      >
        {options.map((option) => {
          const current = option.key === scope;
          return (
            <Link
              key={option.key}
              to={option.to}
              onClick={onNavigate}
              // The current option is marked for assistive tech as well as
              // visually — colour alone is not a state.
              //
              // `"true"`, not `"page"`. This is a SECTION toggle, not a link to
              // the page being viewed: an operator on `/admin/users` with
              // Individual selected is not on `/admin/console`, which is where
              // this option points. `"page"` would announce the wrong thing on
              // every admin screen except the two landing routes.
              aria-current={current ? "true" : undefined}
              className={cn(
                // 44px, matching the nav links this selector sits directly above
                // in both hosts (`SidebarNav.tsx`). It shipped at 36px, which is
                // off the 8px scale AND 8px shorter than its own siblings — on
                // the mobile drawer that is a touch target smaller than every
                // other control in the same column, for no reason the mockup
                // gives: `admin-scope-toggle` sets no height at all.
                "min-h-[44px] rounded-[var(--radius-sm)] px-3 py-2 text-sm font-semibold transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--bg-secondary)]",
                current
                  ? "bg-[color:var(--bg-primary)] text-[color:var(--text-primary)]"
                  : "text-[color:var(--text-tertiary)] hover:text-[color:var(--text-primary)]",
              )}
            >
              {option.label}
            </Link>
          );
        })}
      </div>
      <p className="px-1 text-xs text-[color:var(--text-tertiary)]">
        {adminScopeHint(pathname, scope)}
      </p>
    </div>
  );
}
